import * as React from 'react';
import axios from 'axios';
import Partner from '@app/api/partner/partner.actions';
import { TextInput, FormGroup } from '@patternfly/react-core';
import { Card, CardBody, CardHeader, Col } from 'reactstrap';
import { connect } from 'react-redux';
import { State } from '@app/store/root-reducer';
import { actionCreators as sessionActionCreators } from '@app/common/session/session.reducer';
import UserSession from '@app/common/user-session';
import './Admin.css';

// Declare Google Maps global variable
declare global {
  interface Window {
    google: any;
    initMap: () => void;
  }
}

interface PartnerInfoState {
  partnerName: string;
  contactName: string;
  contactEmail: string;
  partnerAddress: string;
  partnerAddress2: string;
  partnerCity: string;
  partnerState: string;
  partnerCountry: string;
  partnerPostalCode: string;
  isDirty: boolean;
  message: string;
  // Address autofill state
  addressSuggestions: any[];
  showAddressSuggestions: boolean;
  isLoadingAddressSuggestions: boolean;
}

interface Props {
  partnerId: string;
  notificationEnqueue: any;
}

class PartnerInfo extends React.Component<Props, PartnerInfoState> {
  addressSearchTimeout: any = null;
  addressCache: Map<string, any[]> = new Map();
  autocompleteService: any = null;

  constructor(props) {
    super(props);

    this.state = {
      partnerName: "",
      contactName: "",
      contactEmail: "",
      partnerAddress: "",
      partnerAddress2: "",
      partnerCity: "",
      partnerState: "",
      partnerCountry: "",
      partnerPostalCode: "",
      isDirty: false,
      message: "",
      // Address autofill state
      addressSuggestions: [],
      showAddressSuggestions: false,
      isLoadingAddressSuggestions: false,
    }

    // Bind methods
    this.handlePartnerAddressChange = this.handlePartnerAddressChange.bind(this);
    this.selectAddressSuggestion = this.selectAddressSuggestion.bind(this);
    this.hideAddressSuggestions = this.hideAddressSuggestions.bind(this);
    this.searchAddress = this.searchAddress.bind(this);
    this.initGoogleMaps = this.initGoogleMaps.bind(this);
  }

  componentDidMount = async () => {
    this.loadPartnerInfo();
    this.loadGoogleMapsScript();
  }

  componentWillUnmount() {
    if (this.addressSearchTimeout) {
      clearTimeout(this.addressSearchTimeout);
    }
  }

  loadGoogleMapsScript() {
    // Check if Google Maps is already loaded
    if (window.google && window.google.maps) {
      this.initGoogleMaps();
      return;
    }

    // Check if script is already being loaded
    if (document.querySelector('script[src*="maps.googleapis.com"]')) {
      // Script is already loading, wait for it
      const checkGoogleMaps = setInterval(() => {
        if (window.google && window.google.maps) {
          clearInterval(checkGoogleMaps);
          this.initGoogleMaps();
        }
      }, 100);
      return;
    }

    // Load Google Maps script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places&region=CA`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      this.initGoogleMaps();
    };
    document.head.appendChild(script);
  }

  initGoogleMaps() {
    if (window.google && window.google.maps && window.google.maps.places) {
      this.autocompleteService = new window.google.maps.places.AutocompleteService();
    }
  }

  // Parse Google Place details into our address format
  parseGooglePlaceDetails(place: any) {
    const components = place.address_components || [];
    
    const getComponent = (types: string[]) => {
      const component = components.find((comp: any) => 
        types.some((type: string) => comp.types.includes(type))
      );
      return component ? component.long_name : '';
    };

    let state = getComponent(['administrative_area_level_1']);
    let country = getComponent(['country']);

    // Handle Canadian provinces - normalize Quebec variations
    if (country === 'Canada' || country === 'CA') {
      country = 'Canada';
      
      // Handle Quebec variations (French/English)
      if (state === 'Québec' || state === 'Quebec' || state === 'QC') {
        state = 'Quebec';
      }
      // Handle other Canadian provinces
      else if (state === 'Ontario' || state === 'ON') {
        state = 'Ontario';
      }
      else if (state === 'British Columbia' || state === 'BC') {
        state = 'British Columbia';
      }
      else if (state === 'Alberta' || state === 'AB') {
        state = 'Alberta';
      }
      // Add more province mappings as needed
    }

    return {
      house_number: getComponent(['street_number']),
      road: getComponent(['route']),
      city: getComponent(['locality', 'sublocality', 'administrative_area_level_3']),
      state: state,
      country: country,
      postcode: getComponent(['postal_code'])
    };
  }

  // Google Places search
  async searchWithGooglePlaces(query: string) {
    if (!this.autocompleteService) {
      this.setState({
        addressSuggestions: [],
        showAddressSuggestions: false,
        isLoadingAddressSuggestions: false,
      });
      return;
    }

    try {
      const request = {
        input: query,
        types: ['address'],
      };

      this.autocompleteService.getPlacePredictions(request, (predictions: any, status: any) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          const suggestions = predictions.map((prediction: any) => ({
            display_name: prediction.description,
            place_id: prediction.place_id,
            address: {},
            isGooglePlace: true 
          }));

          this.addressCache.set(query.toLowerCase().trim(), suggestions);
          
          this.setState({
            addressSuggestions: suggestions,
            showAddressSuggestions: suggestions.length > 0,
            isLoadingAddressSuggestions: false,
          });
        } else {
          this.setState({
            addressSuggestions: [],
            showAddressSuggestions: false,
            isLoadingAddressSuggestions: false,
          });
        }
      });

    } catch (error) {
      console.error('Google Places error:', error);
      this.setState({
        addressSuggestions: [],
        showAddressSuggestions: false,
        isLoadingAddressSuggestions: false,
      });
    }
  }

  // Get Google Place details
  async getGooglePlaceDetails(placeId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!window.google || !window.google.maps) {
        reject(new Error('Google Maps not loaded'));
        return;
      }

      const service = new window.google.maps.places.PlacesService(document.createElement('div'));
      service.getDetails({
        placeId: placeId,
        fields: ['address_components', 'formatted_address']
      }, (place: any, status: any) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          resolve(place);
        } else {
          reject(new Error(`Place details request failed: ${status}`));
        }
      });
    });
  }

  selectAddressSuggestion(suggestion: any) {
    // Handle Google Places selection
    if (suggestion.isGooglePlace && suggestion.place_id) {
      this.setState({ isLoadingAddressSuggestions: true });
      
      this.getGooglePlaceDetails(suggestion.place_id)
        .then((place) => {
          const address = this.parseGooglePlaceDetails(place);
          this.fillAddressFields(suggestion.display_name, address);
        })
        .catch((error) => {
          console.error('Error getting place details:', error);
          this.fillAddressFields(suggestion.display_name, {});
        })
        .finally(() => {
          this.setState({ isLoadingAddressSuggestions: false });
        });
    } else {
      this.fillAddressFields(suggestion.display_name, suggestion.address || {});
    }

    this.setState({
      addressSuggestions: [],
      showAddressSuggestions: false,
    });
  }

  // Fill address fields with selected suggestion
  fillAddressFields(displayName: string, address: any) {
    // Extract address components
    const streetNumber = address.house_number || '';
    const streetName = address.road || '';
    let fullAddress = '';
    
    if (streetNumber && streetName) {
      fullAddress = `${streetNumber} ${streetName}`;
    } else if (streetName) {
      fullAddress = streetName;
    } else {
      fullAddress = displayName.split(',')[0].trim();
    }
    
    const city = address.city || '';
    const state = address.state || '';
    const country = address.country || '';
    const postalCode = address.postcode || '';

    // Update address fields
    this.setState({
      partnerAddress: fullAddress,
      partnerCity: city,
      partnerState: state,
      partnerCountry: country,
      partnerPostalCode: postalCode,
      isDirty: true
    });
  }

  hideAddressSuggestions() {
    setTimeout(() => {
      this.setState({ showAddressSuggestions: false });
    }, 200);
  }

  // Address autocomplete functionality
  async searchAddress(query: string) {
    if (!query || query.length < 1) {
      this.setState({
        addressSuggestions: [],
        showAddressSuggestions: false,
        isLoadingAddressSuggestions: false,
      });
      return;
    }

    // Check cache first
    const cacheKey = query.toLowerCase().trim();
    if (this.addressCache.has(cacheKey)) {
      const cachedResults = this.addressCache.get(cacheKey) || [];
      this.setState({
        addressSuggestions: cachedResults,
        showAddressSuggestions: cachedResults.length > 0,
        isLoadingAddressSuggestions: false,
      });
      return;
    }

    this.setState({ isLoadingAddressSuggestions: true });
    
    await this.searchWithGooglePlaces(query);
  }

  loadPartnerInfo = async () => {
    try {
      const partner = await Partner.getPartner();
      if (partner) {
        this.setState(() => ({
          partnerName: partner.name || "",
          contactName: partner.billingContact || "",
          contactEmail: partner.billingEmail || "",
          partnerAddress: partner.billingAddress1 || "",
          partnerAddress2: partner.billingAddress2 || "",
          partnerCity: partner.billingCity || "",
          partnerState: partner.billingState || "",
          partnerCountry: partner.billingCountry || "",
          partnerPostalCode: partner.billingPostalCode || ""
        }));
      }
    }
    catch (error) {
      this.setState(() => ({
        message: "There was an error getting the partner information"
      }));
    }
  }

  handlePartnerNameChange = (value: string) => {
    this.setState(() => ({ partnerName: value, isDirty: true }));
  }

  handleContactNameChange = (value: string) => {
    this.setState(() => ({ contactName: value, isDirty: true }));
  }

  handleContactEmailChange = (value: string) => {
    this.setState(() => ({ contactEmail: value, isDirty: true }));
  }

  handlePartnerAddressChange = (value: string) => {
    this.setState(() => ({ partnerAddress: value, isDirty: true }));

    if (this.addressSearchTimeout) {
      clearTimeout(this.addressSearchTimeout);
    }

    this.addressSearchTimeout = setTimeout(() => {
      this.searchAddress(value);
    }, 50);
  }

  handlePartnerAddress2Change = (value: string) => {
    this.setState(() => ({ partnerAddress2: value, isDirty: true }));
  }

  handlePartnerCityChange = (value: string) => {
    this.setState(() => ({ partnerCity: value, isDirty: true }));
  }

  handlePartnerStateChange = (value: string) => {
    this.setState(() => ({ partnerState: value, isDirty: true }));
  }

  handlePartnerCountryChange = (value: string) => {
    this.setState(() => ({ partnerCountry: value, isDirty: true }));
  }

  handlePartnerPostalCodeChange = (value: string) => {
    this.setState(() => ({ partnerPostalCode: value, isDirty: true }));
  }

  handleSubmit = async () => {
    try {
      // Update partner information
      const partnerChanges = {
        name: this.state.partnerName,
        billingContact: this.state.contactName,
        billingEmail: this.state.contactEmail,
        billingAddress1: this.state.partnerAddress,
        billingAddress2: this.state.partnerAddress2,
        billingCity: this.state.partnerCity,
        billingState: this.state.partnerState,
        billingCountry: this.state.partnerCountry,
        billingPostalCode: this.state.partnerPostalCode
      };

      await Partner.updatePartner(partnerChanges);

      this.setState(() => ({
        message: "Partner information updated successfully!",
        isDirty: false
      }));

      // Trigger notification
      this.props.notificationEnqueue({
        message: 'Partner Information Updated',
        options: {
          key: new Date().getTime() + Math.random(),
          variant: 'success',
        },
      });
    }
    catch (error) {
      console.log(error)
      this.setState(() => ({
        message: "There was an error updating the partner information"
      }));
    }
  }

  render() {
    const { partnerName, contactName, contactEmail, partnerAddress, partnerAddress2, partnerCity, partnerState, partnerCountry, partnerPostalCode, addressSuggestions, showAddressSuggestions, isLoadingAddressSuggestions } = this.state;
    const { partnerId } = this.props;

    return (
      <Col lg={6} md={6} sm={12} xs={12} className="branding-card-container">
        <Card className="font-12 partner-info-card">
          <CardHeader>
            <div className="w-100 d-flex justify-content-between">
              <div className="icon-heading-container">
                <div className="icon-heading">
                  Partner Information
                </div>
              </div>
              <div className="text-muted" style={{fontSize: '0.7rem'}}>
                <strong>Partner ID:</strong>&nbsp; {partnerId}
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <table>
              <tbody>
                <tr className="preferences-row">
                  <td className="preferences-label">Name</td>
                  <td>
                    <FormGroup className="form-item" style={{ marginBottom: 0 }}>
                      <TextInput
                        type="text"
                        id="partnerName"
                        name="partnerName"
                        value={partnerName}
                        onChange={this.handlePartnerNameChange}
                        className="text-input form-control"
                        placeholder="Partner Name"
                      />
                    </FormGroup>
                  </td>
                </tr>
                <tr className="preferences-row">
                  <td className="preferences-label">Contact Name</td>
                  <td>
                    <FormGroup className="form-item" style={{ marginBottom: 0 }}>
                      <TextInput
                        type="text"
                        id="contactName"
                        name="contactName"
                        value={contactName}
                        onChange={this.handleContactNameChange}
                        className="text-input form-control"
                        placeholder="Contact Name"
                      />
                    </FormGroup>
                  </td>
                </tr>
                <tr className="preferences-row">
                  <td className="preferences-label">Contact Email</td>
                  <td>
                    <FormGroup className="form-item" style={{ marginBottom: 0 }}>
                      <TextInput
                        type="email"
                        id="contactEmail"
                        name="contactEmail"
                        value={contactEmail}
                        onChange={this.handleContactEmailChange}
                        className="text-input form-control"
                        placeholder="Contact Email"
                      />
                    </FormGroup>
                  </td>
                </tr>
                <tr className="preferences-row">
                  <td className="preferences-label">Address</td>
                  <td style={{ position: 'relative' }}>
                    <div style={{ display: 'flex', gap: '4px', width: '100%' }}>
                      <FormGroup className="form-item" style={{ flex: '1 1 50%', marginBottom: 0, minWidth: 0, position: 'relative' }}>
                        <TextInput
                          type="text"
                          id="partnerAddress"
                          name="partnerAddress"
                          value={partnerAddress}
                          onChange={this.handlePartnerAddressChange}
                          onBlur={this.hideAddressSuggestions}
                          className="text-input form-control"
                          placeholder="Start typing address..."
                          autoComplete="off"
                        />
                        
                        {/* Address Suggestions Dropdown */}
                        {showAddressSuggestions && (
                          <div 
                            style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              backgroundColor: 'white',
                              border: '1px solid #ccc',
                              borderRadius: '4px',
                              maxHeight: '120px',
                              overflowY: 'auto',
                              zIndex: 1000,
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                          >
                            {isLoadingAddressSuggestions ? (
                              <div style={{ padding: '6px', textAlign: 'center', color: '#666', fontSize: '10px' }}>
                                Loading addresses...
                              </div>
                            ) : (
                              addressSuggestions.map((suggestion, index) => (
                                <div
                                  key={index}
                                  style={{
                                    padding: '6px',
                                    cursor: 'pointer',
                                    borderBottom: index < addressSuggestions.length - 1 ? '1px solid #eee' : 'none',
                                    fontSize: '10px'
                                  }}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => this.selectAddressSuggestion(suggestion)}
                                  onMouseEnter={(e) => {
                                    if (e.currentTarget) {
                                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (e.currentTarget) {
                                      e.currentTarget.style.backgroundColor = 'white';
                                    }
                                  }}
                                >
                                  <div style={{ fontSize: '10px', fontWeight: '500' }}>
                                    {suggestion.display_name}
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </FormGroup>
                      <FormGroup className="form-item" style={{ flex: '1 1 50%', marginBottom: 0, minWidth: 0 }}>
                        <TextInput
                          type="text"
                          id="partnerAddress2"
                          name="partnerAddress2"
                          value={partnerAddress2}
                          onChange={this.handlePartnerAddress2Change}
                          className="text-input form-control"
                          placeholder="Address Line 2"
                        />
                      </FormGroup>
                    </div>
                  </td>
                </tr>
                <tr className="preferences-row">
                  <td className="preferences-label">City / State</td>
                  <td>
                    <div style={{ display: 'flex', gap: '4px', width: '100%' }}>
                      <FormGroup className="form-item" style={{ flex: '1 1 50%', marginBottom: 0, minWidth: 0 }}>
                        <TextInput
                          type="text"
                          id="partnerCity"
                          name="partnerCity"
                          value={partnerCity}
                          onChange={this.handlePartnerCityChange}
                          className="text-input form-control"
                          placeholder="City"
                        />
                      </FormGroup>
                      <FormGroup className="form-item" style={{ flex: '1 1 50%', marginBottom: 0, minWidth: 0 }}>
                        <TextInput
                          type="text"
                          id="partnerState"
                          name="partnerState"
                          value={partnerState}
                          onChange={this.handlePartnerStateChange}
                          className="text-input form-control"
                          placeholder="State"
                        />
                      </FormGroup>
                    </div>
                  </td>
                </tr>
                <tr className="preferences-row">
                  <td className="preferences-label">Country / Postal</td>
                  <td>
                    <div style={{ display: 'flex', gap: '4px', width: '100%' }}>
                      <FormGroup className="form-item" style={{ flex: '1 1 50%', marginBottom: 0, minWidth: 0 }}>
                        <TextInput
                          type="text"
                          id="partnerCountry"
                          name="partnerCountry"
                          value={partnerCountry}
                          onChange={this.handlePartnerCountryChange}
                          className="text-input form-control"
                          placeholder="Country"
                        />
                      </FormGroup>
                      <FormGroup className="form-item" style={{ flex: '1 1 50%', marginBottom: 0, minWidth: 0 }}>
                        <TextInput
                          type="text"
                          id="partnerPostalCode"
                          name="partnerPostalCode"
                          value={partnerPostalCode}
                          onChange={this.handlePartnerPostalCodeChange}
                          className="text-input form-control"
                          placeholder="Postal Code"
                        />
                      </FormGroup>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </CardBody>
        </Card>
      </Col>
    );
  }
}

// Create a ConnectedPartnerInfo component with proper ref forwarding
const ConnectedPartnerInfo = connect(
  (state: State) => {
    // Check if user is impersonating a partner space
    const isImpersonating = UserSession.getParam('isImpersonating');
    
    // If impersonating, get partnerId from UserSession (localStorage)
    // Otherwise, get it from Redux store
    const partnerId = isImpersonating 
      ? UserSession.getPartnerId() 
      : state.common.auth.session?.partnerId;
    
    return {
      partnerId,
    };
  },
  {
    notificationEnqueue: sessionActionCreators.notificationEnqueue
  },
  null,
  { forwardRef: true }
)(PartnerInfo);

// Create the final component with forwardRef that exposes handleSubmit and isDirty
const PartnerInfoWithRef = React.forwardRef<{ handleSubmit: () => void; isDirty: () => boolean }, {}>((props, ref) => {
  const partnerInfoRef = React.useRef<PartnerInfo>(null);
  
  React.useImperativeHandle(ref, () => ({
    handleSubmit: () => partnerInfoRef.current?.handleSubmit(),
    isDirty: () => partnerInfoRef.current?.state.isDirty || false
  }));
  
  return <ConnectedPartnerInfo {...props} ref={partnerInfoRef} />;
});

export default PartnerInfoWithRef;
